import { Plus, X } from "lucide-react";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "../../../../../components/ui/common/button";
import { Form } from "../../../../../components/ui/common/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../../components/ui/common/table";
import { FormInputNumber } from "../../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../../components/ui/Input/FormSelector/FormSelect";
import { FuelUse } from "../../../../../types/prodcut-data-review/product-data.types";

// ── Numeric energy-unit label map (no dedicated endpoint exists) ──────────────
const ENERGY_UNIT_LABEL: Record<number, string> = {
  1: "MJ/ha",
  2: "MJ/kg",
  3: "kWh/ha",
  4: "kWh/kg",
  5: "L/ha",
  6: "L/kg"
};
const ENERGY_UNIT_OPTIONS = Object.entries(ENERGY_UNIT_LABEL).map(([value, label]) => ({
  value,
  label
}));

interface FuelUseRowFormValues {
  energySource: string;
  energyUsed: string;
  energyUnit: string;
}

interface FuelUseRowProps {
  fuelUse: FuelUse;
  energySources: any[];
  editing: boolean;
  lookupText: (list: any[], id: number | string) => string;
  onUpdate: (id: string | null, patch: Partial<FuelUse>) => void;
  onRemove: (id: string | null) => void;
}

const FuelUseRow: React.FC<FuelUseRowProps> = ({
  fuelUse,
  energySources,
  editing,
  lookupText,
  onUpdate,
  onRemove
}) => {
  const form = useForm<FuelUseRowFormValues>({
    defaultValues: {
      energySource: String(fuelUse.energySource),
      energyUsed: fuelUse.energyUsed === 0 ? "" : String(fuelUse.energyUsed),
      energyUnit: String(fuelUse.energyUnit)
    }
  });

  useEffect(() => {
    const subscription = form.watch(data => {
      onUpdate(fuelUse.id, {
        energySource: Number(data.energySource),
        energyUsed: data.energyUsed === "" ? 0 : Number(data.energyUsed),
        energyUnit: Number(data.energyUnit)
      });
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  return (
    <Form {...form}>
      <TableRow>
        <TableCell className="[&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
          {editing ? (
            <FormSelect
              control={form.control}
              name="energySource"
              label=""
              placeholder="Select source"
              options={energySources}
            />
          ) : (
            <span className="tw-text-sm">
              {lookupText(energySources, fuelUse.energySource) || "—"}
            </span>
          )}
        </TableCell>
        <TableCell className="[&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
          {editing ? (
            <FormInputNumber control={form.control} name="energyUsed" label="" />
          ) : (
            <span className="tw-text-sm tw-font-mono">
              {fuelUse.energyUsed !== 0 ? fuelUse.energyUsed : "—"}
            </span>
          )}
        </TableCell>
        <TableCell className="[&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
          {editing ? (
            <FormSelect
              control={form.control}
              name="energyUnit"
              label=""
              placeholder="Unit"
              options={ENERGY_UNIT_OPTIONS}
            />
          ) : (
            <span className="tw-text-sm">{ENERGY_UNIT_LABEL[fuelUse.energyUnit] || "—"}</span>
          )}
        </TableCell>
        <TableCell className="tw-text-right">
          {editing && (
            <Button
              variant="ghost"
              size="icon"
              className="tw-h-7 tw-w-7"
              onClick={() => onRemove(fuelUse.id)}
              title="Remove"
            >
              <X className="tw-h-3.5 tw-w-3.5" />
            </Button>
          )}
        </TableCell>
      </TableRow>
    </Form>
  );
};

interface FuelUseSectionProps {
  fuelUses: FuelUse[];
  energySources: any[];
  editing: boolean;
  subSectionHeader: string;
  lookupText: (list: any[], id: number | string) => string;
  onAdd: () => void;
  onUpdate: (id: string | null, patch: Partial<FuelUse>) => void;
  onRemove: (id: string | null) => void;
}

const FuelUseSection: React.FC<FuelUseSectionProps> = ({
  fuelUses,
  energySources,
  editing,
  subSectionHeader,
  lookupText,
  onAdd,
  onUpdate,
  onRemove
}) => {
  return (
    <div className="tw-border-t">
      <div className={subSectionHeader}>
        <span className="tw-text-xs tw-font-semibold tw-text-primary tw-uppercase tw-tracking-wide">
          Fuel Use
        </span>
        {editing && (
          <Button variant="outline" size="sm" className="tw-h-7 tw-text-xs" onClick={onAdd}>
            <Plus className="tw-h-3 tw-w-3 tw-mr-1" />
            Add fuel use
          </Button>
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="tw-text-xs">Energy Source</TableHead>
            <TableHead className="tw-text-xs">Energy Used</TableHead>
            <TableHead className="tw-text-xs">Unit</TableHead>
            <TableHead className="tw-text-xs tw-w-[80px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fuelUses.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="tw-text-xs tw-text-muted-foreground tw-italic">
                No fuel use recorded.
              </TableCell>
            </TableRow>
          )}
          {fuelUses.map(f => (
            <FuelUseRow
              key={f.id}
              fuelUse={f}
              energySources={energySources}
              editing={editing}
              lookupText={lookupText}
              onUpdate={onUpdate}
              onRemove={onRemove}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default FuelUseSection;
